import React, { useEffect, useState, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { ledgerService } from '../../services/api';
import { Container, Paper, Typography, Box, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TableContainer, TextField, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../contexts/AuthContext';

const AccountPage = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'moderator' || currentUser?.role === 'superadmin';
  const [account, setAccount] = useState(null);
  const [txs, setTxs] = useState([]);
  const [allTxs, setAllTxs] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [actionLoading, setActionLoading] = useState({ id: null, type: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTx, setDetailTx] = useState(null);
  const totalExchangedHours = txs.filter(t => t.status === 'approved').reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
  const approvedGlobalHours = allTxs.filter(t => t.status === 'approved').reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
  const negativeBalanceGlobalHours = allAccounts.reduce((sum, a) => {
    const v = Number(a.balance_hours) || 0;
    return sum + (v < 0 ? -v : 0);
  }, 0);
  const globalTotalHours = approvedGlobalHours + negativeBalanceGlobalHours;

  const exportExcel = async () => {
    const rows = txs.filter(t => ['approved','rejected'].includes(t.status)).map(t => ({
      data: new Date(t.created_at).toLocaleString(),
      da: t.provider_label,
      a: t.receiver_label,
      servizio: `${t.category_label}${t.subcategory_label ? ' / ' + t.subcategory_label : ''}`,
      descrizione: t.description || '',
      ore: Number(t.hours) || 0,
      stato: t.status,
    }));
    const approvedTotal = txs.filter(t => t.status === 'approved').reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    const rejectedTotal = txs.filter(t => t.status === 'rejected').reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    const grandTotal = approvedTotal + rejectedTotal;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(`Transazioni ${year}`);
    ws.columns = [
      { header: 'Data', key: 'data', width: 20 },
      { header: 'Da', key: 'da', width: 28 },
      { header: 'A', key: 'a', width: 28 },
      { header: 'Servizio', key: 'servizio', width: 34 },
      { header: 'Descrizione', key: 'descrizione', width: 36 },
      { header: 'Ore', key: 'ore', width: 10 },
      { header: 'Stato', key: 'stato', width: 14 },
    ];
    ws.spliceRows(1, 0, [`TimeBank: Scambi ${year}`]);
    ws.mergeCells(1, 1, 1, 7);
    const titleRow = ws.getRow(1);
    titleRow.font = { bold: true, size: 14 };
    titleRow.alignment = { horizontal: 'center' };
    rows.forEach(r => ws.addRow(r));
    const approvedRow = ws.addRow({ data: '', da: '', a: '', servizio: '', descrizione: '', ore: approvedTotal, stato: 'Ore approvate' });
    const rejectedRow = ws.addRow({ data: '', da: '', a: '', servizio: '', descrizione: '', ore: rejectedTotal, stato: 'Ore rifiutate' });
    const totalRow = ws.addRow({ data: '', da: '', a: '', servizio: '', descrizione: '', ore: grandTotal, stato: 'Totale' });

    ws.getRow(2).font = { bold: true };
    ws.eachRow((row, rowNumber) => {
      const isFooter = rowNumber > 2 + rows.length;
      row.eachCell(cell => {
        const hasValue = cell.value !== null && cell.value !== undefined && cell.value !== '';
        cell.border = hasValue || !isFooter ? {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        } : undefined;
      });
    });

    approvedRow.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCFFCC' } }; });
    rejectedRow.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCCCC' } }; });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transazioni_${year}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const acc = await ledgerService.getAccount();
      const list = await ledgerService.listTransactions({ year });
      const all = await ledgerService.listTransactions();
      const accounts = isAdmin ? await ledgerService.getAllAccounts() : { data: [] };
      setAccount(acc.data);
      setTxs(list.data);
      setAllTxs(all.data);
      setAllAccounts(accounts.data);
    } catch (e) {
      setError('Errore nel caricamento del conto ore');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!isAdmin) return;
    setError('');
    setActionLoading({ id, type: 'approve' });
    try {
      await ledgerService.approveTransaction(id);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      setError(detail || 'Errore durante l’approvazione dello scambio');
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const reject = async (id) => {
    if (!isAdmin) return;
    setError('');
    setActionLoading({ id, type: 'reject' });
    try {
      await ledgerService.rejectTransaction(id);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      setError(detail || 'Errore durante il rifiuto dello scambio');
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const filteredTxs = txs.filter((t) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const fields = [
      new Date(t.created_at).toLocaleString().toLowerCase(),
      String(t.provider_label || '').toLowerCase(),
      String(t.receiver_label || '').toLowerCase(),
      String(t.category_label || '').toLowerCase(),
      String(t.subcategory_label || '').toLowerCase(),
      String(t.description || '').toLowerCase(),
      String(t.status || '').toLowerCase(),
      String(t.hours || '').toLowerCase(),
    ];
    return fields.some((f) => f.includes(q));
  });

  const hasRowActions = isAdmin && filteredTxs.some((t) => t.status === 'pending');

  const renderStatus = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'approved') return <Chip size="small" color="success" variant="outlined" label="Approvato" />;
    if (normalized === 'rejected') return <Chip size="small" color="error" variant="outlined" label="Rifiutato" />;
    if (normalized === 'pending') return <Chip size="small" color="warning" variant="outlined" label="In attesa" />;
    return <Chip size="small" variant="outlined" label={normalized || '—'} />;
  };

  const formatHours = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
  };

  const openDetail = (tx) => {
    setDetailTx(tx);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailTx(null);
  };

  return (
    <Container component="main" maxWidth="false">
<Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 } }}>
         <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          {isAdmin && (
            <>
              <Typography variant="subtitle1" sx={{ alignSelf: 'flex-end', mb: 0.5, width: '100%', textAlign: 'right' }}>
                Totale ore scambiate: <strong>{totalExchangedHours} h</strong>
              </Typography>
              <Typography variant="body2" sx={{ alignSelf: 'flex-end', mb: 0.5, width: '100%', textAlign: 'right' }}>
                Ore totali positive (utenti): <strong>{approvedGlobalHours} h</strong>
              </Typography>
              <Typography variant="body2" sx={{ alignSelf: 'flex-end', mb: 1, width: '100%', textAlign: 'right' }}>
                Ore totali negative (utenti): <strong>{negativeBalanceGlobalHours} h</strong>
              </Typography>
              <Typography variant="body2" sx={{ alignSelf: 'flex-end', mb: 1, width: '100%', textAlign: 'right' }}>
                Totale ore (utenti): <strong>{globalTotalHours} h</strong>
              </Typography>
            </>
          )}
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Il tuo conto ore
          </Typography>
        </Box>

  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

  {loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Saldo: <strong>{account?.balance_hours ?? 0} h</strong>
      </Typography>
      {isAdmin && null}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography variant="body1">Movimenti</Typography>
          <Box sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <TextField
              label="Anno"
              type="number"
              size="small"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())}
              sx={{ width: 110 }}
              inputProps={{ inputMode: 'numeric' }}
            />
            {isAdmin && (
              <Button variant="contained" color="success" onClick={exportExcel}>
                Esporta in Excel
              </Button>
            )}
            <TextField
              label="Cerca"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="nome, email, servizio, stato..."
              sx={{ width: { xs: '100%', sm: 320 }, flexGrow: { xs: 1, sm: 0 } }}
            />
          </Box>
        </Box>

      {!isMobile ? (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 170, whiteSpace: 'nowrap' }}>Data</TableCell>
                  <TableCell sx={{ width: 210 }}>Da</TableCell>
                  <TableCell sx={{ width: 210 }}>A</TableCell>
                  <TableCell sx={{ width: 260 }}>Servizio</TableCell>
                  <TableCell sx={{ width: 320 }}>Descrizione</TableCell>
                  <TableCell align="right" sx={{ width: 90, whiteSpace: 'nowrap' }}>Ore</TableCell>
                  <TableCell sx={{ width: 130, whiteSpace: 'nowrap' }}>Stato</TableCell>
                  {hasRowActions && <TableCell align="right" sx={{ width: 200 }}>Azioni</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(filteredTxs.length > 0 ? filteredTxs : []).map((t) => (
                  <TableRow key={t.id} onClick={() => openDetail(t)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{t.provider_label}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{t.receiver_label}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {t.category_label}{t.subcategory_label ? ` / ${t.subcategory_label}` : ''}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{t.description || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatHours(t.hours)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderStatus(t.status)}</TableCell>
                    {hasRowActions && (
                      <TableCell align="right">
                        {t.status === 'pending' ? (
                          <Box sx={{ display: 'inline-flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); approve(t.id); }}
                              disabled={actionLoading.id === t.id}
                              startIcon={actionLoading.id === t.id && actionLoading.type === 'approve' ? <CircularProgress size={16} /> : null}
                            >
                              {actionLoading.id === t.id && actionLoading.type === 'approve' ? '' : 'Approva'}
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); reject(t.id); }}
                              disabled={actionLoading.id === t.id}
                              startIcon={actionLoading.id === t.id && actionLoading.type === 'reject' ? <CircularProgress size={16} /> : null}
                            >
                              {actionLoading.id === t.id && actionLoading.type === 'reject' ? '' : 'Rifiuta'}
                            </Button>
                          </Box>
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredTxs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={hasRowActions ? 8 : 7}>
                      <Typography variant="body2" align="center" sx={{ py: 2 }}>Nessun movimento trovato.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {filteredTxs.length > 0 ? (
            filteredTxs.map((t) => (
              <Paper key={t.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(t.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Da: <strong>{t.provider_label || '-'}</strong>
                </Typography>
                <Typography variant="body2">
                  A: <strong>{t.receiver_label || '-'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  Servizio: {t.category_label}{t.subcategory_label ? ` / ${t.subcategory_label}` : ''}
                </Typography>
                <Typography variant="body2">Ore: {formatHours(t.hours)}</Typography>
                <Box sx={{ mt: 1 }}>{renderStatus(t.status)}</Box>
                <Box sx={{ mt: 1.5 }}>
                  <Button variant="outlined" size="small" onClick={() => openDetail(t)}>
                    Dettaglio descrizione
                  </Button>
                </Box>
                {hasRowActions && t.status === 'pending' && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => approve(t.id)}
                      disabled={actionLoading.id === t.id}
                      startIcon={actionLoading.id === t.id && actionLoading.type === 'approve' ? <CircularProgress size={16} /> : null}
                    >
                      {actionLoading.id === t.id && actionLoading.type === 'approve' ? '' : 'Approva'}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => reject(t.id)}
                      disabled={actionLoading.id === t.id}
                      startIcon={actionLoading.id === t.id && actionLoading.type === 'reject' ? <CircularProgress size={16} /> : null}
                    >
                      {actionLoading.id === t.id && actionLoading.type === 'reject' ? '' : 'Rifiuta'}
                    </Button>
                  </Box>
                )}
              </Paper>
            ))
          ) : (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="body2" align="center">Nessun movimento trovato.</Typography>
            </Paper>
          )}
        </Box>
      )}
    <Dialog open={detailOpen} onClose={closeDetail}>
      <DialogTitle>Descrizione movimento</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {detailTx?.description ? detailTx.description : 'Nessuna descrizione'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDetail}>Chiudi</Button>
      </DialogActions>
    </Dialog>
    </>
  )}
</Paper>

    </Container>
  );
};

export default AccountPage;
